import React, { useState, useEffect } from 'react';
import { Box, Tab } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useNavigate, useParams,useLocation } from 'react-router-dom';
import { BASE_URL } from "../apiConfig";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CheckIcon from '@mui/icons-material/Check';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ChevronLeft } from 'lucide-react';
import './columntoproject.css';

const ColumnSelection = () => {
  const {projectId} = useParams(); 
  const {roleId} = useParams();
  const {clientID} = useParams();
  
  const navigate = useNavigate();
  const location = useLocation();

  // State for the active tab
  const [value, setValue] = useState('1');

  // State for column data (to be fetched from API)
  const [columnsData, setColumnsData] = useState([]);

  // State for selected columns in each tab
  const [basicDataSelections, setBasicDataSelections] = useState({});
  const [workDataSelections, setWorkDataSelections] = useState({});
  const [performanceDataSelections, setPerformanceDataSelections] = useState({});

  // State for loading status
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [inputSheet, setInputSheet] = useState('');
  
  // Modal state
  const [showModal, setShowModal] = useState(false);

  const [clientName, setClientName] = useState("");

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  useEffect(() => {
          if (location.state && location.state.clientName) {
              setClientName(location.state.clientName);
              console.log("Client Name:", location.state.clientName);
          }
      }, [location.state]);

  // Fetch columns data from API
  useEffect(() => {
    const fetchColumnsData = async () => {
      try {
        // Get the auth token from localStorage
        const token = localStorage.getItem('accessToken');

        if (!token) {
          console.error("No authentication token found");
          return;
        }

        // Make the API call with proper authorization
        const response = await fetch(`${BASE_URL}/project/get_all_columns?project_id=${projectId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`API call failed with status: ${response.status}`);
        }

        const data = await response.json();
        console.log(roleId);
        setColumnsData(data);

        // Initialize selections based on the API response
        const basicData = data.filter(item => item.sheet_name === "Customer Employee Basic data");
        const workData = data.filter(item => item.sheet_name === "Customer Employee Work data");
        const performanceData = data.filter(item => item.sheet_name === "Customer Performance Data");

        // Initialize the state with selections
        const initialBasicData = {};
        const initialWorkData = {};
        const initialPerformanceData = {};

        basicData.forEach(column => {
          initialBasicData[column.id] = {
            selected: column.is_mandatory || column.is_mandatory === true,
            mandatory: column.data_mandatory,
            details: column.client_column_name || column.column_name,
            disabled: column.is_mandatory === true,
            column_type: column.column_type || "string"
          };
        });

        workData.forEach(column => {
          initialWorkData[column.id] = {
            selected: column.is_mandatory || column.is_mandatory === true,
            mandatory: column.data_mandatory,
            details: column.client_column_name || column.column_name,
            disabled: column.is_mandatory === true,
            column_type: column.column_type || "string"
          };
        });

        performanceData.forEach(column => {
          initialPerformanceData[column.id] = {
            selected: column.is_mandatory || column.is_mandatory === true,
            mandatory: column.data_mandatory,
            details: column.client_column_name || column.column_name,
            disabled: column.is_mandatory === true,
            column_type: column.column_type || "string"
          };
        });

        setBasicDataSelections(initialBasicData);
        setWorkDataSelections(initialWorkData);
        setPerformanceDataSelections(initialPerformanceData);

        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching columns data:", error);
        setIsLoading(false);
        setErrorMessage("Failed to load columns data. Please try again.");
      }
    };

    fetchColumnsData();
  }, [projectId]);

  // Tab content arrays based on filtered data
  const basicDataColumns = columnsData.filter(item => item.sheet_name === "Customer Employee Basic data")
    .sort((a, b) => a.rank - b.rank);

  const workDataColumns = columnsData.filter(item => item.sheet_name === "Customer Employee Work data")
    .sort((a, b) => a.rank - b.rank);

  const performanceDataColumns = columnsData.filter(item => item.sheet_name === "Customer Performance Data")
    .sort((a, b) => a.rank - b.rank);

  // Handlers for checkbox changes
  const handleInputChange = (id, tabIndex) => {
    if (tabIndex === 0) {
      setBasicDataSelections(prev => {
        if (prev[id]?.disabled) return prev;

        const newState = { ...prev };
        newState[id] = {
          ...newState[id],
          selected: !newState[id].selected
        };

        if (!newState[id].selected) {
          newState[id].mandatory = false;
          setColumnErrors(prevErrors => ({ ...prevErrors, [id]: false }));
        }

        return newState;
      });
    } else if (tabIndex === 1) {
      setWorkDataSelections(prev => {
        if (prev[id]?.disabled) return prev;

        const newState = { ...prev };
        newState[id] = {
          ...newState[id],
          selected: !newState[id].selected
        };

        if (!newState[id].selected) {
          newState[id].mandatory = false;
          setColumnErrors(prevErrors => ({ ...prevErrors, [id]: false }));
        }

        return newState;
      });
    } else {
      setPerformanceDataSelections(prev => {
        if (prev[id]?.disabled) return prev;

        const newState = { ...prev };
        newState[id] = {
          ...newState[id],
          selected: !newState[id].selected
        };

        if (!newState[id].selected) {
          newState[id].mandatory = false;
          setColumnErrors(prevErrors => ({ ...prevErrors, [id]: false }));
        }

        return newState;
      });
    }
  };

  const handleMandatoryChange = (id, tabIndex) => {
    if (tabIndex === 0) {
      setBasicDataSelections(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          mandatory: !prev[id].mandatory
        }
      }));
    } else if (tabIndex === 1) {
      setWorkDataSelections(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          mandatory: !prev[id].mandatory
        }
      }));
    } else {
      setPerformanceDataSelections(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          mandatory: !prev[id].mandatory
        }
      }));
    }
  };

  const handleDetailsChange = (id, value, tabIndex) => {
    setColumnErrors(prev => ({ ...prev, [id]: value.trim() === '' }));

    if (tabIndex === 0) {
      setBasicDataSelections(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          details: value
        }
      }));
    } else if (tabIndex === 1) {
      setWorkDataSelections(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          details: value
        }
      }));
    } else {
      setPerformanceDataSelections(prev => ({
        ...prev,
        [id]: {
          ...prev[id],
          details: value
        }
      }));
    }
  };

  // Handle next button click
  const handleNext = () => {
    const currentSelections = value === '1' ? basicDataSelections : workDataSelections;
    const errors = {};
    let hasError = false;

    Object.entries(currentSelections).forEach(([id, col]) => {
      if (col.selected && (!col.details || col.details.trim() === '')) {
        errors[id] = true;
        hasError = true;
      }
    });

    if (hasError) {
      setColumnErrors(prev => ({ ...prev, ...errors }));
      setErrorMessage('Column name cannot be empty. Please fill in all selected column names.');
      return;
    }

    setErrorMessage('');
    const nextValue = String(parseInt(value) + 1);
    if (parseInt(value) < 3) {
      setValue(nextValue);
    }
  };

  // Handle downloading the Excel sheet
  const handleDownloadExcel = () => {
    if (inputSheet) {
      window.location.href = inputSheet;
    }
  };

  // Handle submitting columns to project
  const handleSubmit = async () => {
    const errors = {};
    let hasError = false;

    [basicDataSelections, workDataSelections, performanceDataSelections].forEach(selections => {
      Object.entries(selections).forEach(([id, col]) => {
        if (col.selected && (!col.details || col.details.trim() === '')) {
          errors[id] = true;
          hasError = true;
        }
      });
    });

    if (hasError) {
      setColumnErrors(prev => ({ ...prev, ...errors }));
      setErrorMessage('Column name cannot be empty. Please fill in all selected column names.');
      return;
    }

    try {
      setIsSaving(true);
      setErrorMessage('');
      
      // Get the auth token
      const token = localStorage.getItem('accessToken');

      if (!token) {
        console.error("No authentication token found");
        setErrorMessage("Authentication token not found. Please log in again.");
        setIsSaving(false);
        return;
      }

      // Prepare data for column_to_project API
      const columnsToSave = [
        ...Object.entries(basicDataSelections)
          .filter(([_, value]) => value.selected)
          .map(([id, value]) => ({
            column_id: id,
            project_id: projectId,
            client_column_name: value.details,
            add: true,
            data_mandatory: value.mandatory,
            column_type: value.column_type || "string"
          })),
        ...Object.entries(workDataSelections)
          .filter(([_, value]) => value.selected)
          .map(([id, value]) => ({
            column_id: id,
            project_id: projectId,
            client_column_name: value.details,
            add: true,
            data_mandatory: value.mandatory,
            column_type: value.column_type || "string"
          })),
        ...Object.entries(performanceDataSelections)
          .filter(([_, value]) => value.selected)
          .map(([id, value]) => ({
            column_id: id,
            project_id: projectId,
            client_column_name: value.details,
            add: true,
            data_mandatory: value.mandatory,
            column_type: value.column_type || "string"
          }))
      ];

      // Save columns to project
      const saveResponse = await fetch(`${BASE_URL}/project/column_to_project`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(columnsToSave)
      });

      if (!saveResponse.ok) {
        throw new Error(`Saving columns failed with status: ${saveResponse.status}`);
      }

      const data = await saveResponse.json();
      setInputSheet(data.input_sheet);
      
      // Show success modal
      setShowModal(true);
      setIsSaving(false);
    } catch (error) {
      console.error("Error in save process:", error);
      setErrorMessage(`Failed: ${error.message}. Please try again.`);
      setIsSaving(false);
    }
  };

  // State for column name validation errors
  const [columnErrors, setColumnErrors] = useState({});

  // State for download process
  const [downloadState, setDownloadState] = useState('initial'); // 'initial', 'downloading', 'complete'

  // Success Modal Component
  const SuccessModal = () => {
    if (!showModal) return null;
    
    if (downloadState === 'downloading') {
      return (
        <div className="column-modal-overlay">
          <div className="column-new-modal" style={{ width: '491px', height: '247px' }}>
            <div className="column-new-modal-icon-container">
              <FileDownloadIcon style={{ fontSize: '36px', color: '#C12328' }} />
            </div>
            <div className="column-new-modal-content">
              <h2>Downloading Excel Sheet</h2>
              <p>Please Wait<span className="column-loading-dots"></span></p>
            </div>
          </div>
        </div>
      );
    }
    
    if (downloadState === 'complete') {
      return (
        <div className="column-modal-overlay">
          <div className="column-new-modal" style={{ width: '491px', height: '247px' }}>
            <div className="column-new-modal-icon-container">
              <CheckIcon style={{ fontSize: '36px', color: '#C12328' }} />
            </div>
            <div className="column-new-modal-content">
              <h2>Excel Sheet Download</h2>
              <button 
                className="column-back-to-project-btn"
                onClick={() => {
                  setShowModal(false);
                  navigate(`/architect/${roleId}/projects`);
                }}
              >
                <ArrowBackIcon style={{ marginRight: '8px' }} /> Back to Project History
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    // Initial success modal
    return (
      <div className="column-modal-overlay">
        <div className="column-new-modal" style={{ width: '491px', height: '247px' }}>
          <div className="column-new-modal-icon-container">
            <DescriptionIcon style={{ fontSize: '36px', color: '#C12328' }} />
          </div>
          <div className="column-new-modal-content">
            <h2>Input Sheet Saved Successfully</h2>
            <button 
              className="column-download-excel-btn" 
              onClick={() => {
                setDownloadState('downloading');
                
                // Simulate download process
                setTimeout(() => {
                  handleDownloadExcel();
                  setDownloadState('complete');
                }, 2000);
              }}
            >
              Download Excel Sheet <FileDownloadIcon style={{ marginLeft: '8px' }} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <div className="column-loading">Loading...</div>;
  }

  return (
    <div className="column-container">
      <div className="column-header">
        <button className="column-back-button" onClick={() => navigate(`/architect/${clientID}/${roleId}/${projectId}/kpi`,{
          state: { clientName }
        })}>
          <ChevronLeft style={{ size: "16px" }} /> Choose Columns
        </button>
      </div>

      {errorMessage && (
        <div className="column-error-message">
          {errorMessage}
        </div>
      )}

      <TabContext value={value}>
        <Box sx={{ borderBottom: 0, borderColor: 'divider', display: "flex", justifyContent: "center" }}>
          <TabList
            style={{ display: "flex", justifyContent: "center", borderBottom: 1, borderColor: 'divider' }}
            onChange={handleChange}
            aria-label="column selection tabs"
            TabIndicatorProps={{
              style: { backgroundColor: '#C12328' }, // indicator color
            }}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontFamily: 'Inter',
                fontSize: '14px',
                minHeight: '64px',
                padding: '16px 12px',
                color: 'black', // default color for non-selected tabs
              },
              '& .MuiTab-root.Mui-selected': {
                color: '#C12328', // selected tab color
              },
            }}
          >
            <Tab
              label={<div style={{ textAlign: 'center' }}>Customer Employee<br />Basic Data</div>}
              value="1"
            />
            <Tab
              label={<div style={{ textAlign: 'center' }}>Customer Employee<br />Work Data</div>}
              value="2"
            />
            <Tab
              label={<div style={{ textAlign: 'center' }}>Customer Performance<br />Data</div>}
              value="3"
            />
          </TabList>
        </Box>

        <TabPanel value="1" sx={{ padding: '24px' }}>
          <h2 className="column-section-title">Customer Employee Basic Data</h2>
          <div className="column-table">
            <div className="column-table-header">
              <div className="column-header-input">Input</div>
              <div className="column-header-mandatory">Data Mandatory</div>
              <div className="column-header-details">Details</div>
            </div>

            {basicDataColumns.map(column => (
              <div className="column-table-row" key={column.id}>
                <div className="column-cell-input">
                  <input
                    type="checkbox"
                    className="column-checkbox"
                    checked={basicDataSelections[column.id]?.selected || false}
                    onChange={() => handleInputChange(column.id, 0)}
                    disabled={basicDataSelections[column.id]?.disabled || false}
                  />
                  <span>{column.column_name}</span>
                </div>
                <div className="column-cell-mandatory">
                  <input
                    type="checkbox"
                    className="mandatory-column-checkbox"
                    checked={basicDataSelections[column.id]?.mandatory || false}
                    disabled={!basicDataSelections[column.id]?.selected || basicDataSelections[column.id]?.disabled || false}
                    onChange={() => handleMandatoryChange(column.id, 0)}
                  />
                </div>
                <div className="column-cell-details">
                  <input
                    type="text"
                    className={`column-details-input${columnErrors[column.id] ? ' column-details-input-error' : ''}`}
                    placeholder="Enter column name"
                    value={basicDataSelections[column.id]?.details || ''}
                    disabled={!basicDataSelections[column.id]?.selected}
                    onChange={(e) => handleDetailsChange(column.id, e.target.value, 0)}
                  />
                  {columnErrors[column.id] && (
                    <span className="column-details-error-text">Column name cannot be empty</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="column-footer">
            <button className="column-next-button" onClick={handleNext}>Next</button>
          </div>
        </TabPanel>

        <TabPanel value="2" sx={{ padding: '24px' }}>
          <h2 className="column-section-title">Customer Employee Work Data</h2>
          <div className="column-table">
            <div className="column-table-header">
              <div className="column-header-input">Input</div>
              <div className="column-header-mandatory">Data Mandatory</div>
              <div className="column-header-details">Details</div>
            </div>

            {workDataColumns.map(column => (
              <div className="column-table-row" key={column.id}>
                <div className="column-cell-input">
                  <input
                    type="checkbox"
                    className="column-checkbox"
                    checked={workDataSelections[column.id]?.selected || false}
                    onChange={() => handleInputChange(column.id, 1)}
                    disabled={workDataSelections[column.id]?.disabled || false}
                  />
                  <span>{column.column_name}</span>
                </div>
                <div className="column-cell-mandatory">
                  <input
                    type="checkbox"
                    className="mandatory-column-checkbox"
                    checked={workDataSelections[column.id]?.mandatory || false}
                    disabled={!workDataSelections[column.id]?.selected || workDataSelections[column.id]?.disabled || false}
                    onChange={() => handleMandatoryChange(column.id, 1)}
                  />
                </div>
                <div className="column-cell-details">
                  <input
                    type="text"
                    className={`column-details-input${columnErrors[column.id] ? ' column-details-input-error' : ''}`}
                    placeholder="Enter column name"
                    value={workDataSelections[column.id]?.details || ''}
                    disabled={!workDataSelections[column.id]?.selected}
                    onChange={(e) => handleDetailsChange(column.id, e.target.value, 1)}
                  />
                  {columnErrors[column.id] && (
                    <span className="column-details-error-text">Column name cannot be empty</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="column-footer">
            <button className="column-next-button" onClick={handleNext}>Next</button>
          </div>
        </TabPanel>

        <TabPanel value="3" sx={{ padding: '24px' }}>
          <h2 className="column-section-title">Customer Performance Data</h2>
          <div className="column-table">
            <div className="column-table-header">
              <div className="column-header-input">Input</div>
              <div className="column-header-mandatory">Data Mandatory</div>
              <div className="column-header-details">Details</div>
            </div>

            {performanceDataColumns.map(column => (
              <div className="column-table-row" key={column.id}>
                <div className="column-cell-input">
                  <input
                    type="checkbox"
                    className="column-checkbox"
                    checked={performanceDataSelections[column.id]?.selected || false}
                    onChange={() => handleInputChange(column.id, 2)}
                    disabled={performanceDataSelections[column.id]?.disabled || false}
                  />
                  <span>{column.column_name}</span>
                </div>
                <div className="column-cell-mandatory">
                  <input
                    type="checkbox"
                    className="mandatory-column-checkbox"
                    checked={performanceDataSelections[column.id]?.mandatory || false}
                    disabled={!performanceDataSelections[column.id]?.selected || performanceDataSelections[column.id]?.disabled || false}
                    onChange={() => handleMandatoryChange(column.id, 2)}
                  />
                </div>
                <div className="column-cell-details">
                  <input
                    type="text"
                    className={`column-details-input${columnErrors[column.id] ? ' column-details-input-error' : ''}`}
                    placeholder="Enter column name"
                    value={performanceDataSelections[column.id]?.details || ''}
                    disabled={!performanceDataSelections[column.id]?.selected}
                    onChange={(e) => handleDetailsChange(column.id, e.target.value, 2)}
                  />
                  {columnErrors[column.id] && (
                    <span className="column-details-error-text">Column name cannot be empty</span>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="column-footer">
            {isSaving ? (
              <button className="column-submit-button" disabled>
                Processing...
              </button>
            ) : (
              <button className="column-submit-button" onClick={handleSubmit}>
                Submit
              </button>
            )}
          </div>
        </TabPanel>
      </TabContext>

      {/* Success Modal */}
      <SuccessModal />
    </div>
  );
};

export default ColumnSelection;